<!-- WelcomeHero.vue -->

<template>
  <div class="relative overflow-hidden">
    <!-- Split Background -->
    <div class="absolute inset-0">
      <div class="absolute inset-0 bg-white"></div>
      <div
        class="absolute top-0 right-0 w-1/2 h-full bg-logo-color hidden lg:block"
        style="clip-path: polygon(15% 0, 100% 0, 100% 100%, 0% 100%)"
      ></div>
    </div>

    <!-- Main Content -->
    <div class="relative z-10 min-h-screen">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="min-h-screen flex flex-col lg:flex-row items-center gap-12 lg:gap-8 py-20 lg:py-0"
        >
          <!-- Left Content -->
          <div
            class="flex-1 lg:pr-12"
            data-aos="fade-right"
            data-aos-duration="1000"
          >
            <!-- Badge -->
            <div class="flex items-center gap-3 mb-8">
              <div class="w-12 h-[2px] bg-logo-color"></div>
              <span
                class="text-sm font-bold text-logo-color uppercase tracking-widest"
              >
                Non-Profit Organisation
              </span>
            </div>

            <!-- Main Heading -->
            <h1
              class="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span class="text-logo-color">Welcome to</span>
              <br />
              <span class="text-gray-900">Youth Ally</span>
            </h1>

            <!-- Description -->
            <p class="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
              A non-profit organisation committed to promoting ethical and
              transformative leadership among young people and equipping them
              with the skills to create dignified economic opportunities for
              themselves and their communities.
            </p>

            <!-- CTA Buttons -->
            <div class="flex flex-wrap gap-4 mb-12">
              <button
                @click="toggleModal"
                class="group px-8 py-4 bg-logo-color text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-logo-color/30 transition-all duration-300 flex items-center gap-3"
              >
                <span>Get In Touch</span>
                <i
                  class="fas fa-arrow-right group-hover:translate-x-1 transition-transform"
                ></i>
              </button>
              <router-link
                to="/about"
                class="group px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-logo-color hover:text-logo-color transition-all duration-300 flex items-center gap-3"
              >
                <span>Learn More</span>
                <i class="fas fa-chevron-right text-sm"></i>
              </router-link>
            </div>

            <!-- Decorative Feature Pills -->
            <div class="flex flex-wrap gap-3">
              <div
                class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
              >
                <span class="w-2 h-2 bg-logo-color rounded-full"></span>
                <span class="text-sm font-medium text-logo-color"
                  >Ethical Leadership</span
                >
              </div>
              <div
                class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
              >
                <span class="w-2 h-2 bg-logo-color rounded-full"></span>
                <span class="text-sm font-medium text-logo-color"
                  >Youth Empowerment</span
                >
              </div>
              <div
                class="flex items-center gap-2 px-4 py-2 bg-logo-color/10 rounded-full"
              >
                <span class="w-2 h-2 bg-logo-color rounded-full"></span>
                <span class="text-sm font-medium text-logo-color"
                  >Skills Development</span
                >
              </div>
            </div>
          </div>

          <!-- Right Content - Image Collage -->
          <div
            class="flex-1 relative"
            data-aos="fade-left"
            data-aos-duration="1000"
            data-aos-delay="200"
          >
            <div class="relative">
              <!-- Main Large Image -->
              <div class="relative rounded-2xl overflow-hidden shadow-2xl z-10">
                <img
                  src="@/assets/education.jpg"
                  alt="Youth Innovation"
                  class="w-full h-[350px] lg:h-[450px] object-cover"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"
                ></div>
                <!-- Overlay Content -->
                <div class="absolute bottom-0 left-0 right-0 p-6">
                  <div class="flex items-end justify-between">
                    <div class="text-white">
                      <div class="text-sm font-medium opacity-80 mb-1">
                        Our Focus
                      </div>
                      <div class="text-xl font-bold text-center">
                        Transforming Youth Leadership
                      </div>
                    </div>
                    <router-link
                      to="/services"
                      class="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-logo-color hover:text-white text-logo-color transition-all duration-300"
                    >
                      <i class="fas fa-arrow-right"></i>
                    </router-link>
                  </div>
                </div>
              </div>

              <!-- Floating Small Images -->
              <div
                class="absolute -bottom-10 right-20 w-36 h-36 rounded-2xl overflow-hidden shadow-xl border-4 border-white z-20 hidden lg:block"
              >
                <img
                  src="@/assets/Explore2.jpg"
                  alt="STEM"
                  class="w-full h-full object-cover"
                />
              </div>

              <div
                class="absolute -top-8 -right-8 w-36 h-36 rounded-2xl overflow-hidden shadow-xl border-4 border-white z-20 hidden lg:block"
              >
                <img
                  src="@/assets/Explore3.jpg"
                  alt="Empowerment"
                  class="w-full h-full object-cover"
                />
              </div>

              <!-- Decorative Elements -->
              <div
                class="absolute -bottom-4 -right-4 w-full h-full bg-logo-color/20 rounded-2xl -z-10 hidden lg:block"
              ></div>

              <!-- Floating Badge -->
              <div
                class="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-lg z-30 flex items-center gap-2"
              >
                <span
                  class="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                ></span>
                <span class="text-sm font-semibold text-gray-700">
                  Making Impact
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Modal -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="showContactModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm"
          @click="closeModal"
        ></div>

        <!-- Modal Content -->
        <div
          class="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          <!-- Header -->
          <div class="bg-logo-color px-6 py-8 text-white text-center">
            <div
              class="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <i class="fas fa-headset text-3xl"></i>
            </div>
            <h3 class="text-2xl font-bold">Get In Touch</h3>
            <p class="text-white/80 mt-2">We'd love to hear from you</p>
          </div>

          <!-- Close Button -->
          <button
            @click="closeModal"
            class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>

          <!-- Contact Options -->
          <div class="p-6 space-y-4">
            <!-- Email -->
            <a
              :href="'mailto:' + contactInfo.email"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all group"
            >
              <div
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors"
              >
                <i
                  class="fas fa-envelope text-logo-color group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">Email</div>
                <div class="text-gray-800 font-semibold">
                  {{ contactInfo.email }}
                </div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-logo-color transition-colors"
              ></i>
            </a>

            <!-- Phone -->
            <a
              :href="'tel:' + contactInfo.phone"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-logo-color hover:bg-logo-color/5 transition-all group"
            >
              <div
                class="w-12 h-12 bg-logo-color/10 rounded-xl flex items-center justify-center group-hover:bg-logo-color transition-colors"
              >
                <i
                  class="fas fa-phone-alt text-logo-color group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">Phone</div>
                <div class="text-gray-800 font-semibold">
                  {{ contactInfo.phone }}
                </div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-logo-color transition-colors"
              ></i>
            </a>

            <!-- WhatsApp -->
            <a
              href="https://wa.me/+233249372583"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center p-4 rounded-xl border-2 border-gray-100 hover:border-green-500 hover:bg-green-50 transition-all group"
            >
              <div
                class="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors"
              >
                <i
                  class="fab fa-whatsapp text-xl text-green-600 group-hover:text-white transition-colors"
                ></i>
              </div>
              <div class="ml-4 flex-1">
                <div class="text-sm text-gray-500">WhatsApp</div>
                <div class="text-gray-800 font-semibold">Quick Response</div>
              </div>
              <i
                class="fas fa-chevron-right text-gray-400 group-hover:text-green-500 transition-colors"
              ></i>
            </a>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "WelcomeHero",
  data() {
    return {
      showContactModal: false,
      contactInfo: {
        email: "support@youthallynow.org",
        phone: "+233 24 937 2583",
      },
    };
  },
  methods: {
    toggleModal() {
      this.showContactModal = !this.showContactModal;
    },
    closeModal() {
      this.showContactModal = false;
    },
  },
};
</script>

<style scoped></style>
